// ─────────────────────────────────────────────────────────────────────────────
// RowMenu — the ⋯ button on a node row and the menu it opens:
//   View Details · Edit Node │ Copy Node ID · Delete Node
//
// The menu is PORTALLED to <body> and positioned fixed against the button's
// bounding box. It cannot live inside the cell: NodesTable's wrapper is
// `overflow-x: auto`, which also clips vertically, so an in-flow dropdown would
// be cut off on the last rows or push the table into a scroll.
//
// Because it's fixed, it must close on scroll/resize rather than try to follow.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./rowActions.module.css";
import { EyeIcon, PencilIcon, CopyIcon, CheckIcon, TrashIcon } from "./icons";

const MENU_WIDTH = 196;
const GAP = 6;

export default function RowMenu({ node, actions }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const [copied, setCopied] = useState(false);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Anchor under the button, right-aligned; flip above if it would overflow.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) return;
    const r = btnRef.current.getBoundingClientRect();
    const below = window.innerHeight - r.bottom;
    const flip = below < 190 && r.top > below;
    setPos({
      top: flip ? undefined : r.bottom + GAP,
      bottom: flip ? window.innerHeight - r.top + GAP : undefined,
      left: Math.max(GAP, r.right - MENU_WIDTH),
    });
  }, [open]);

  // Close on outside click, Escape, or any scroll/resize (the menu is fixed and
  // would otherwise detach from its row).
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    const onDown = (e) => {
      if (menuRef.current?.contains(e.target) || btnRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  // Reset the "Copied" affordance so it reads as a transient confirmation.
  useEffect(() => {
    if (!copied) return undefined;
    const t = setTimeout(() => setCopied(false), 1_500);
    return () => clearTimeout(t);
  }, [copied]);

  const run = (fn) => (e) => {
    e.stopPropagation();
    setOpen(false);
    fn();
  };

  const copyId = async () => {
    try {
      await navigator.clipboard.writeText(node.node_id ?? "");
      setCopied(true);
    } catch {
      // Clipboard is blocked on plain http in some browsers. The id is visible
      // in the row and selectable, so don't claim a copy that didn't happen.
      setCopied(false);
    }
  };

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={styles.kebabBtn}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Row actions"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
      >
        ⋯
      </button>

      {open && pos && createPortal(
        <div ref={menuRef} className={styles.menu} style={pos} role="menu">
          <button className={styles.menuItem} role="menuitem"
            onClick={run(() => actions.onViewDetails(node))}>
            <span className={styles.menuIcon}><EyeIcon /></span> View Details
          </button>
          {/* Edit and Delete WRITE. They need read_write on node_monitoring
              (Admin / Security Admin), so a read-only viewer doesn't get them —
              the backend would refuse anyway, and offering an action that always
              fails is worse than not offering it. View Details and Copy Node ID
              stay: both are reads. */}
          {actions.canWrite && (
            <button className={styles.menuItem} role="menuitem"
              onClick={run(() => actions.onEdit(node))}>
              <span className={styles.menuIcon}><PencilIcon /></span> Edit Node
            </button>
          )}

          <div className={styles.menuSep} />

          {/* Stays open long enough to show the confirmation, then closes. */}
          <button className={styles.menuItem} role="menuitem"
            onClick={(e) => { e.stopPropagation(); copyId(); }}>
            <span className={styles.menuIcon}>{copied ? <CheckIcon /> : <CopyIcon />}</span>
            {copied ? "Copied" : "Copy Node ID"}
          </button>
          {actions.canWrite && (
            <button className={`${styles.menuItem} ${styles.menuItemDanger}`} role="menuitem"
              onClick={run(() => actions.onDelete(node))}>
              <span className={styles.menuIcon}><TrashIcon /></span> Delete Node
            </button>
          )}
        </div>,
        document.body,
      )}
    </>
  );
}
